/*
 * ALAS YENİ ALGORİTMA - OPTİMİZE EDİLMİŞ VERSİYON V3
 * 
 * Kullanıcı geribildirimlerine göre iyileştirildi:
 * 1. Doğrulama Oranı Tutarlılığı: Her zaman (toplam kütle / gerekli kütle) × 100
 * 2. YENİ OPTİMİZASYON: Minimum toplam kütle (birincil), sonra minimum ünite sayısı (ikincil)
 * 3. Test için: 2x2x2m oda → 2x IP 530 (742g), değil 1x IP 1400 (840g)
 * 4. Yükseklik filtresi uygulanıyor
 * 5. Maksimum 3 farklı ürün tipi
 * 6. Dinamik kombinasyon değerlendirmesi: tek, ikili ve üçlü ürün kombinasyonları
 */

import { getExtinguisherProducts } from './productData.js';
import { getParameterByCode } from './parameterData.js';

// Sabitler
const UNITS_PER_CONTROLLER = 12;
const MIN_VERIFICATION_RATIO = 1.00; // %100 minimum doğrulama oranı
// Ürün başı adet limiti kaldırıldı; konfigürasyonla yönetilecek
const DEFAULT_MAX_UNITS_PER_PRODUCT = Infinity;
const DEFAULT_MAX_PRODUCT_TYPES = 3; // Maksimum ürün tipi sayısı
const DEFAULT_SEARCH_SLACK = 10; // Arama toleransı (performans koruması)

/**
 * Excel uyumlu yuvarlama fonksiyonu
 * Excel: frac > 0.5 → ROUNDUP, frac ≤ 0.5 → ROUNDDOWN
 * Sonrasında %100 minimum doğrulama garantisi: totalMass < requiredMass ise +1
 */
function excelRound(value) {
  const frac = value - Math.floor(value);
  return frac > 0.5 ? Math.ceil(value) : Math.floor(value);
}

// ES2020 olmayan ortamlarda derleme uyumluluğu için nullish coalesce yardımcı fonksiyonu
const coalesce = (a, b) => (a !== undefined && a !== null ? a : b);

/**
 * Ana hesaplama fonksiyonu
 */
export const calculateWithAlasAlgorithm = (input) => {
  const { length, width, height, fireClass, safetyFactor, optimization, maxUnitsPerProduct, maxProductTypes, searchSlack } = input;
  // Dinamik konfigürasyon (Excel yaklaşımı: varsayılan minUnits)
  const config = {
    optimization: optimization || 'minUnits',
    maxUnitsPerProduct: coalesce(maxUnitsPerProduct, DEFAULT_MAX_UNITS_PER_PRODUCT),
    maxProductTypes: coalesce(maxProductTypes, DEFAULT_MAX_PRODUCT_TYPES),
    searchSlack: coalesce(searchSlack, DEFAULT_SEARCH_SLACK)
  };
  
  // 1. Temel hesaplamalar
  const volume = length * width * height;
  const adjustedVolume = volume * safetyFactor;
  const density = getExtinguishingDensity(fireClass);
  const requiredMass = adjustedVolume * density;

  // 2. Yükseklik filtresi (cm'ye çevir)
  const roomHeightCm = height * 100;
  const allProducts = getExtinguisherProducts();
  const filteredProducts = filterProductsByHeight(allProducts, roomHeightCm);
  
  if (filteredProducts.length === 0) {
    throw new Error(`${height}m yüksekliğe uygun ürün bulunamadı`);
  }

  // 3. Optimal çözümü bul - tamamen yeniden yazıldı
  const solution = findOptimalSolutionNew(filteredProducts, requiredMass, config);
  
  if (!solution) {
    throw new Error('Gereksinimları karşılayan çözüm bulunamadı');
  }

  // 4. AFS-CON1 sayısını hesapla
  const totalUnits = solution.products.reduce((sum, p) => sum + p.quantity, 0);
  const controllerCount = Math.ceil(totalUnits / UNITS_PER_CONTROLLER);

  // 5. Doğrulama oranını tutarlı hesapla: (toplam kütle / gerekli kütle) × 100
  const verificationRatio = (solution.totalMass / requiredMass * 100);

  // 6. Sonuçları formatla
  return {
    roomData: {
      length,
      width, 
      height,
      volume,
      adjustedVolume,
      fireClass,
      safetyFactor,
      density
    },
    calculation: {
      requiredMass: Math.round(requiredMass),
      totalMass: solution.totalMass,
      verificationRatio: verificationRatio.toFixed(2),
      totalUnits,
      controllerCount
    },
    products: solution.products.map(p => ({
      id: p.product.id,
      model: p.product.model,
      name: p.product.name,
      quantity: p.quantity,
      unitMass: p.product.effective_mass,
      totalMass: p.quantity * p.product.effective_mass,
      class_a_capacity: p.product.class_a_capacity || 0,
      class_b_capacity: p.product.class_b_capacity || 0,
      effective_mass: p.product.effective_mass,
      min_height: p.product.min_height,
      max_height: p.product.max_height,
      mass: p.product.mass
    })),
    controllers: [{
      id: 99,
      model: "AFS-CON1",
      name: "Alas Aerosol Sıralayıcı Elektronik Modül",
      quantity: controllerCount,
      isController: true,
      class_a_capacity: 0,
      class_b_capacity: 0,
      effective_mass: 0
    }],
    summary: {
      totalCost: solution.totalMass,
      efficiency: solution.totalMass / requiredMass,
      optimization: `Optimizasyon: ${config.optimization} | Ünite: ${totalUnits} | Kütle: ${solution.totalMass}g`
    }
  };
};

/**
 * Yükseklik filtreleme - Oda yüksekliğine uygun ürünleri filtrele
 */
function filterProductsByHeight(products, roomHeightCm) {
  return products.filter(product => {
    // Yükseklik bilgisi yoksa ürünü dahil et
    if (!product.max_height) return true;
    
    const maxHeightCm = product.max_height * 100;
    
    // Excel uyumu: sadece max_height kontrolü yapılır
    // Excel formülü: IF(maxHeight >= odaYüksekliği, "X", "")
    return roomHeightCm <= maxHeightCm;
  });
}

/**
 * YENİ OPTIMAL ÇÖZÜM BULMA ALGORİTMASI - V3
 * 
 * Kullanıcının önerileri doğrultusunda optimizasyon kriterleri güncellendi:
 * 1. En düşük toplam kütle (birincil öncelik - verimli kaynak kullanımı)
 * 2. En az ünite sayısı (ikincil öncelik - eşit kütlede daha az ünite)
 * 3. En az ürün tipi sayısı (üçüncül öncelik)
 * 4. Dinamik kombinasyon taraması: tek, ikili ve üçlü ürün kombinasyonları
 */
function findOptimalSolutionNew(products, requiredMass, config) {
  // Ürünleri efektif kütleye göre büyükten küçüğe sırala
  const sortedProducts = [...products].sort((a, b) => b.effective_mass - a.effective_mass);
  
  const allSolutions = [];

  // 1. TEK ÜRÜN ÇÖZÜMLERİ
  for (const product of sortedProducts) {
    let unitsNeeded = excelRound(requiredMass / product.effective_mass);
    // %100 minimum doğrulama garantisi
    if (unitsNeeded * product.effective_mass < requiredMass) unitsNeeded++;
    const withinLimit = unitsNeeded <= coalesce(config.maxUnitsPerProduct, DEFAULT_MAX_UNITS_PER_PRODUCT);
    const totalMass = unitsNeeded * product.effective_mass;
    const verificationRatio = totalMass / requiredMass;
    if (verificationRatio >= MIN_VERIFICATION_RATIO && withinLimit) {
      allSolutions.push({
        products: [{ product, quantity: unitsNeeded }],
        totalMass,
        totalUnits: unitsNeeded,
        verificationRatio,
        productTypes: 1,
        largestUnitMass: product.effective_mass,
        description: `${unitsNeeded}x ${product.model}`
      });
    }
  }

  // 2. İKİLİ ÜRÜN KOMBİNASYONLARI
  for (let i = 0; i < sortedProducts.length; i++) {
    for (let j = i; j < sortedProducts.length; j++) {
      const product1 = sortedProducts[i];
      const product2 = sortedProducts[j];
      
      // Her kombinasyon için optimal sayıları bul
      const bestCombo = findBestDualCombination(product1, product2, requiredMass, i === j, config);
      
      if (bestCombo) {
        allSolutions.push(bestCombo);
      }
    }
  }

  // 3. ÜÇLÜ ÜRÜN KOMBİNASYONLARI
  for (let i = 0; i < Math.min(sortedProducts.length, 4); i++) {
    for (let j = i; j < Math.min(sortedProducts.length, 4); j++) {
      for (let k = j; k < Math.min(sortedProducts.length, 4); k++) {
        const product1 = sortedProducts[i];
        const product2 = sortedProducts[j];
        const product3 = sortedProducts[k];
        
        const bestCombo = findBestTripleCombination(product1, product2, product3, requiredMass, i, j, k, config);
        
        if (bestCombo) {
          allSolutions.push(bestCombo);
        }
      }
    }
  }

  // Çözüm bulunamadıysa null döndür
  if (allSolutions.length === 0) {
    return null;
  }

  // 4. EN İYİ ÇÖZÜMÜ SEÇ - Dinamik optimizasyon kriterleri
  allSolutions.sort((a, b) => compareSolutions(a, b, config));
  
  const bestSolution = allSolutions[0];
  
  return bestSolution;
}

/* İkili ürün kombinasyonu için en iyi sayıları bul */
const findBestDualCombination = (product1, product2, requiredMass, isSameProduct, config) => {
  let bestSolution = null;

  const baseMax = Math.ceil(
    requiredMass / Math.min(product1.effective_mass, product2.effective_mass)
  ) + coalesce(config.searchSlack, DEFAULT_SEARCH_SLACK);
  const maxUnits = Math.min(baseMax, coalesce(config.maxUnitsPerProduct, DEFAULT_MAX_UNITS_PER_PRODUCT));

  for (let units1 = 1; units1 <= maxUnits; units1++) {
    const remainingMass = requiredMass - units1 * product1.effective_mass;
    if (remainingMass <= 0) continue;

    let minUnits2 = excelRound(remainingMass / product2.effective_mass);
    // %100 minimum doğrulama garantisi
    if (minUnits2 * product2.effective_mass < remainingMass) minUnits2++;
    const maxUnits2 = Math.min(maxUnits, minUnits2 + coalesce(config.searchSlack, DEFAULT_SEARCH_SLACK));
    const startUnits2 = isSameProduct ? Math.max(minUnits2, units1) : minUnits2;

    for (let units2 = startUnits2; units2 <= maxUnits2; units2++) {
      const totalMass = units1 * product1.effective_mass + units2 * product2.effective_mass;
      const verificationRatio = totalMass / requiredMass;
      if (verificationRatio < MIN_VERIFICATION_RATIO) continue;

      const totalUnits = units1 + units2;
      const candidate = {
        products: [],
        totalMass,
        totalUnits,
        verificationRatio,
        productTypes: isSameProduct ? 1 : 2,
        largestUnitMass: Math.max(product1.effective_mass, product2.effective_mass),
        description: ''
      };

      if (!bestSolution || compareSolutions(candidate, bestSolution, config) < 0) {
        const productsCombination = [];
        let description = '';

        if (isSameProduct) {
          productsCombination.push({ product: product1, quantity: units1 + units2 });
          description = `${units1 + units2}x ${product1.model}`;
        } else {
          productsCombination.push({ product: product1, quantity: units1 });
          productsCombination.push({ product: product2, quantity: units2 });
          description = `${units1}x ${product1.model} + ${units2}x ${product2.model}`;
        }

        bestSolution = {
          products: productsCombination,
          totalMass,
          totalUnits,
          verificationRatio,
          productTypes: isSameProduct ? 1 : 2,
          largestUnitMass: Math.max(product1.effective_mass, product2.effective_mass),
          description
        };
      }
    }
  }

  return bestSolution;
};

/* Üçlü ürün kombinasyonu için en iyi sayıları bul */
const findBestTripleCombination = (product1, product2, product3, requiredMass, i, j, k, config) => {
  let bestSolution = null;
  
  // Dinamik arama sınırı (performans için)
  const maxSearch = Math.min(
    Math.ceil(requiredMass / Math.min(product1.effective_mass, product2.effective_mass, product3.effective_mass)) + coalesce(config.searchSlack, DEFAULT_SEARCH_SLACK),
    coalesce(config.maxUnitsPerProduct, DEFAULT_MAX_UNITS_PER_PRODUCT)
  );
  
  for (let units1 = 1; units1 <= maxSearch; units1++) {
    const remainingMass1 = requiredMass - (units1 * product1.effective_mass);
    if (remainingMass1 <= 0) continue;
    
    for (let units2 = 1; units2 <= maxSearch; units2++) {
      const remainingMass2 = remainingMass1 - (units2 * product2.effective_mass);
      if (remainingMass2 <= 0) continue;
      
      let minUnits3 = excelRound(remainingMass2 / product3.effective_mass);
      // %100 minimum doğrulama garantisi
      if (minUnits3 * product3.effective_mass < remainingMass2) minUnits3++;
      const maxUnits3 = Math.min(maxSearch, minUnits3 + coalesce(config.searchSlack, DEFAULT_SEARCH_SLACK));
      
      for (let units3 = minUnits3; units3 <= maxUnits3; units3++) {
        const totalMass = (units1 * product1.effective_mass) + 
                          (units2 * product2.effective_mass) + 
                          (units3 * product3.effective_mass);
        const verificationRatio = totalMass / requiredMass;
        
        if (verificationRatio >= MIN_VERIFICATION_RATIO) {
          const totalUnits = units1 + units2 + units3;

          const candidate = {
            products: [],
            totalMass,
            totalUnits,
            verificationRatio,
            productTypes: 3,
            largestUnitMass: Math.max(product1.effective_mass, product2.effective_mass, product3.effective_mass),
            description: ''
          };
          if (!bestSolution || compareSolutions(candidate, bestSolution, config) < 0) {
            // Ürün kombinasyonunu oluştur
            const productsCombination = [];
            let description = '';
            
            if (i === j && j === k) {
              productsCombination.push({ product: product1, quantity: units1 + units2 + units3 });
              description = `${units1 + units2 + units3}x ${product1.model}`;
            } else if (i === j) {
              productsCombination.push({ product: product1, quantity: units1 + units2 });
              productsCombination.push({ product: product3, quantity: units3 });
              description = `${units1 + units2}x ${product1.model} + ${units3}x ${product3.model}`;
            } else if (j === k) {
              productsCombination.push({ product: product1, quantity: units1 });
              productsCombination.push({ product: product2, quantity: units2 + units3 });
              description = `${units1}x ${product1.model} + ${units2 + units3}x ${product2.model}`;
            } else {
              productsCombination.push({ product: product1, quantity: units1 });
              productsCombination.push({ product: product2, quantity: units2 });
              productsCombination.push({ product: product3, quantity: units3 });
              description = `${units1}x ${product1.model} + ${units2}x ${product2.model} + ${units3}x ${product3.model}`;
            }
            
            bestSolution = {
              products: productsCombination,
              totalMass,
              totalUnits,
              verificationRatio,
              productTypes: productsCombination.length,
              largestUnitMass: Math.max(product1.effective_mass, product2.effective_mass, product3.effective_mass),
              description
            };
          }
          // Aramaya devam
        }
      }
    }
  }
  
  return bestSolution;
};

/*
 * Çözüm karşılaştırma fonksiyonu (dinamik optimizasyon)
 * optimization: 'minUnits' | 'minMass'
 */
function compareSolutions(a, b, config) {
  const mode = (config && config.optimization) ? config.optimization : 'minUnits';
  if (mode === 'minUnits') {
    // 1. En az ünite sayısı (birincil)
    if (a.totalUnits !== b.totalUnits) return a.totalUnits - b.totalUnits;
    // 2. Eşit ünite sayısında: en az toplam kütle (Deniz Bey: %100 yeterli, fazlası maliyet)
    if (Math.abs(a.totalMass - b.totalMass) > 0.1) return a.totalMass - b.totalMass;
    // 3. En az ürün tipi
    return a.productTypes - b.productTypes;
  } else {
    // minMass yaklaşımı
    if (Math.abs(a.totalMass - b.totalMass) > 0.1) return a.totalMass - b.totalMass;
    if (a.totalUnits !== b.totalUnits) return a.totalUnits - b.totalUnits;
    return a.productTypes - b.productTypes;
  }
}

/* Yangın sınıfına göre söndürme yoğunluğu */
function getExtinguishingDensity(fireClass) {
  const densities = {
    'a-wooden-stack': 49.75,     // A Sınıfı Ahşap Deste
    'b-nheptane-liquid': 74.89,  // B Sınıfı N-Heptan Sıvı  
    'wooden-stack-general': 62,  // Genel Ahşap Deste
    'a-polymer-sheet': 49.39     // A Sınıfı Polimer Levha
  };
  
  return densities[fireClass] || 49.75; // Varsayılan: A Sınıfı Ahşap Deste
}

 